import React, { useState, useEffect, useRef } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api"; // ← Your axios with token interceptor
import { toast } from "react-hot-toast";
import { validateFile } from "../../utils/fileValidation";
import Step1 from "../registrationSteps/Step1";
import Step2 from "../registrationSteps/Step2";
import StepCredentials from "../registrationSteps/StepCredentials";
import Step4 from "../registrationSteps/Step4";
import StepContact from "../registrationSteps/StepContact";
import Step5 from "../registrationSteps/Step5";
import SuccessModal from "../modals/SuccessModal";
import AnalysisSection1 from "../analysisSection/AnalysisSection1";
import AnalysisSection2 from "../analysisSection/AnalysisSection2";
import AnalysisSection3 from "../analysisSection/AnalysisSection3";
import AnalysisSection4 from "../analysisSection/AnalysisSection4";
import AnalysisSection5 from "../analysisSection/AnalysisSection5";
import "../tutorRegistration/TutorRegistration.css";
import { findUnusualRateWarnings } from "../../utils/rateThresholds";

const AcademyRegistration = () => {
  const navigate = useNavigate();
  const { user, updateUser, refreshUser } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const [formData, setFormData] = useState({
    // Profile Picture / Logo
    academyLogo: null,
    coverImage: null,

    // Academy Basic Info
    academyName: "",
    academyEmail: "",
    about: "",
    bio: "",

    // Location
    location: {
      address: "",
      placeId: "",
      lat: 0,
      lng: 0,
    },

    // Documents
    businessDoc: null,
    cacDocument: null,
    accreditationDoc: null,
    adminId: null,

    // Registration Numbers
    registrationNumber: "",
    cacNumber: "",

    // Admin Contact
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminPhone: "",

    // ────── NEW TEACHING STRUCTURE (Step 2) ──────
    selectedCategories: [],
    categoryDetails: {},
    teachingModes: [],
    teachingModeOther: "",

    // Legacy fields (kept for backward compatibility)
    programType: "",
    mainProgram: "",
    selectedCourses: [],
    hourlyRate: "",
    ratePeriod: "hour",
    currency: "NGN",

    // Others
    socialMedia: { platform: "", link: "" },
    additionalLicenses: [],
    isInfepRegistered: false,
    offersIntroSession: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // ── Autosave — same pattern as TutorRegistration.jsx, see that file for
  // the detailed reasoning on each piece (hasDraftData vs registrationStep,
  // the one-time "Draft saved" toast, refreshUser() instead of trusting the
  // cached context user, etc.) ──────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const isDirtyRef = useRef(false);
  const hasResumedRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const hasShownDraftToastRef = useRef(false);

  useEffect(() => {
    return () => clearTimeout(autosaveTimerRef.current);
  }, []);

  // academyName is the authoritative field — firstName mirrors it for
  // institution accounts, lastName is intentionally blank. Never concatenate
  // the two or a stray "Training Center" placeholder ends up in the name.
  const academyName = user?.academyName || user?.firstName || "";

  const validateForm = (step) => {
    let tempErrors = {};
    if (step === 1) {
      // A Google/Facebook sign-up already has a profile photo on the account
      // (see socialLogin) — don't force a fresh upload on top of that.
      if (!formData.academyLogo && !user?.picture?.url)
        tempErrors.academyLogo = "Academy logo is required";
      if (!formData.academyName)
        tempErrors.academyName = "Academy name is required";
      if (
        !formData.academyEmail ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.academyEmail)
      )
        tempErrors.academyEmail = "Valid academy email is required";
      // if (!formData.bio?.trim()) tempErrors.bio = "Bio is required";
      // With this:
      const bioWords = formData.bio?.trim()
        ? formData.bio
            .trim()
            .split(/\s+/)
            .filter((w) => w.length > 0).length
        : 0;

      if (bioWords < 75) {
        tempErrors.bio = `Bio must be at least 75 words (currently ${bioWords})`;
      } else if (bioWords > 150) {
        tempErrors.bio = `Bio must not exceed 150 words (currently ${bioWords})`;
      }
      if (!formData.location?.address?.trim()) {
        tempErrors.address = "Address is required";
      }
    }
    if (step === 2) {
      // if (!formData.registrationNumber)
      //   tempErrors.registrationNumber = "Registration number is required";
      // if (!formData.accreditationDoc)
      //   tempErrors.accreditationDoc = "Accreditation document is required";
      // if (!formData.cacNumber) tempErrors.cacNumber = "CAC number is required";
      // if (!formData.cacDocument)
      //   tempErrors.cacDocument = "CAC document is required";
      // if (formData.isInfepRegistered) {
      //   if (!formData.credentialId)
      //     tempErrors.credentialId = "Credential ID is required";
      //   if (!formData.issuingDate)
      //     tempErrors.issuingDate = "Issuing date is required";
      // }
      if (!formData.selectedCategories?.length) {
        tempErrors.selectedCategories =
          "Please select at least one course category";
      }
    }

    if (step === 3) {
      if (!formData.adminFirstName)
        tempErrors.adminFirstName = "First name is required";
      if (!formData.adminLastName)
        tempErrors.adminLastName = "Last name is required";
      if (
        !formData.adminEmail ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)
      )
        tempErrors.adminEmail = "Valid email is required";
      if (!formData.adminPhone || !/^\+?\d{10,15}$/.test(formData.adminPhone))
        tempErrors.adminPhone = "Valid phone number is required";
      if (!formData.adminId) tempErrors.adminId = "Identity card is required";
    }
    return tempErrors;
  };

  // Shared by final submit, autosave, and step-transition saves — same
  // reasoning as TutorRegistration.jsx's buildFormData. `includeFiles:
  // false` is used for the periodic debounced save so it doesn't re-upload
  // unchanged documents/logo on every autosave tick.
  const buildFormData = ({
    includeFiles = true,
    includeCompletion = false,
    step = currentStep,
  } = {}) => {
    const data = new FormData();

    if (includeFiles) {
      if (formData.academyLogo) data.append("picture", formData.academyLogo);
      if (formData.coverImage) data.append("coverImage", formData.coverImage);
      [
        formData.businessDoc,
        formData.cacDocument,
        formData.accreditationDoc,
        formData.adminId,
      ]
        .filter(Boolean)
        .forEach((file) => data.append("documents", file));
    }

    data.append("academyName", formData.academyName || "");
    data.append("academyEmail", formData.academyEmail || "");
    data.append("bio", formData.bio || "");
    data.append("about", formData.about || formData.bio || "");
    data.append("registrationNumber", formData.registrationNumber || "");
    data.append("cacNumber", formData.cacNumber || "");

    data.append("adminFirstName", formData.adminFirstName || "");
    data.append("adminLastName", formData.adminLastName || "");
    data.append("adminEmail", formData.adminEmail || "");
    if (formData.adminPhone?.trim()) {
      const cleaned = formData.adminPhone.replace(/\D/g, "");
      data.append(
        "adminPhone",
        cleaned.length === 10 ? `+234${cleaned}` : formData.adminPhone,
      );
    }

    data.append(
      "selectedCategories",
      JSON.stringify(formData.selectedCategories || []),
    );
    data.append("categoryDetails", JSON.stringify(formData.categoryDetails || {}));
    data.append("teachingModes", JSON.stringify(formData.teachingModes || []));
    if (formData.teachingModeOther?.trim()) {
      data.append("teachingModeOther", formData.teachingModeOther.trim());
    }

    data.append("programType", formData.programType || "hybrid");
    data.append("currency", formData.currency || "NGN");
    data.append(
      "offersIntroSession",
      formData.offersIntroSession ? "true" : "false",
    );

    data.append("location[address]", formData.location?.address || "");
    data.append("location[placeId]", formData.location?.placeId || "");
    data.append("location[lat]", formData.location?.lat || 0);
    data.append("location[lng]", formData.location?.lng || 0);

    if (formData.socialMedia?.link) {
      data.append("socialMedia", JSON.stringify(formData.socialMedia));
    }
    if (formData.additionalLicenses?.length > 0) {
      data.append(
        "additionalLicenses",
        JSON.stringify(formData.additionalLicenses),
      );
    }

    data.append("registrationStep", String(step));

    if (includeCompletion) {
      data.append("profileCompleted", "true");
      data.append("role", "institution");
    }

    return data;
  };

  // Partial save — used by autosave and step transitions. Silently no-ops
  // if nothing has changed since the last save.
  const saveProgress = async ({ includeFiles = true, step } = {}) => {
    if (!isDirtyRef.current) return;

    setSaveStatus("saving");
    try {
      const data = buildFormData({ includeFiles, includeCompletion: false, step });
      const res = await api.put("/users/me", data);
      updateUser(res.data.user);
      isDirtyRef.current = false;
      setSaveStatus("saved");

      if (!hasShownDraftToastRef.current) {
        hasShownDraftToastRef.current = true;
        toast.success(
          "Draft saved — you can close this tab and continue anytime. Just log back in.",
          { duration: 5000 },
        );
      }
    } catch (err) {
      console.error("Autosave failed:", err);
      setSaveStatus("error");
    }
  };

  // Resume-on-mount — see TutorRegistration.jsx for the full reasoning.
  // hasDraftData, not registrationStep alone, gates whether to prefill:
  // the debounced autosave saves step-1 fields continuously even before
  // the user ever clicks "Continue" (which is the only thing that advances
  // registrationStep), so gating purely on the step counter silently loses
  // real saved data for anyone who never got past step 1.
  useEffect(() => {
    let cancelled = false;

    const resume = async () => {
      const fresh = await refreshUser().catch(() => null);
      if (cancelled) return;

      const source = fresh || user;
      const hasDraftData = Boolean(
        source?.academyName?.trim() ||
          source?.bio?.trim() ||
          source?.location?.address?.trim() ||
          source?.registrationNumber?.trim() ||
          source?.cacNumber?.trim() ||
          source?.adminFirstName?.trim() ||
          source?.selectedCategories?.length ||
          (source?.categoryDetails && Object.keys(source.categoryDetails).length),
      );

      if (source && !source.profileCompleted && hasDraftData) {
        setFormData((prev) => ({
          ...prev,
          academyName: source.academyName || prev.academyName,
          academyEmail: source.academyEmail || prev.academyEmail,
          bio: source.bio || prev.bio,
          about: source.about || prev.about,
          registrationNumber: source.registrationNumber || prev.registrationNumber,
          cacNumber: source.cacNumber || prev.cacNumber,
          adminFirstName: source.adminFirstName || prev.adminFirstName,
          adminLastName: source.adminLastName || prev.adminLastName,
          adminEmail: source.adminEmail || prev.adminEmail,
          adminPhone: source.adminPhone || prev.adminPhone,
          selectedCategories: source.selectedCategories?.length
            ? source.selectedCategories
            : prev.selectedCategories,
          categoryDetails:
            source.categoryDetails && Object.keys(source.categoryDetails).length
              ? source.categoryDetails
              : prev.categoryDetails,
          teachingModes: source.teachingModes?.length
            ? source.teachingModes
            : prev.teachingModes,
          teachingModeOther: source.teachingModeOther || prev.teachingModeOther,
          location: source.location?.address ? source.location : prev.location,
          socialMedia: source.socialMedia?.link
            ? source.socialMedia
            : prev.socialMedia,
          additionalLicenses: source.additionalLicenses?.length
            ? source.additionalLicenses
            : prev.additionalLicenses,
        }));
        setCurrentStep(Math.min(source.registrationStep || 1, totalSteps));
        toast("Welcome back — we restored your saved progress.", { icon: "👋" });
        hasShownDraftToastRef.current = true;
      }
      hasResumedRef.current = true;
    };

    resume();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced autosave — fires ~3s after the user stops typing/changing
  // anything, text fields only.
  useEffect(() => {
    if (!hasResumedRef.current) return; // don't race the resume prefill above
    isDirtyRef.current = true;

    clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      saveProgress({ includeFiles: false });
    }, 3000);

    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const cleanedAdminPhone = formData.adminPhone?.replace(/\D/g, "");
  // console.log("admin phone", cleanedAdminPhone);

  // const handleInputChange = (e) => {
  //   const { name, value } = e.target;

  //   if (name.includes(".")) {
  //     const [parent, child] = name.split(".");
  //     setFormData((prev) => ({
  //       ...prev,
  //       [parent]: {
  //         ...prev[parent],
  //         [child]: value,
  //       },
  //     }));
  //   } else {
  //     setFormData((prev) => ({ ...prev, [name]: value }));
  //   }

  //   if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  // };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("location.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          [field]: value,
        },
      }));
    } else if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleOtherInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [`${field}Other`]: value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const error = validateFile(file, { maxSizeBytes: 2 * 1024 * 1024 });
    if (error) {
      toast.error(error);
      e.target.value = "";
      return;
    }

    setFormData({ ...formData, academyLogo: file });
    if (errors.academyLogo) setErrors({ ...errors, academyLogo: "" });
  };

  // Previously no real validation (just a size check, no mime-type gate) —
  // this is the ID document field for the admin contact, the single
  // biggest source of unexplained save failures on the tutor side (an
  // iPhone HEIC photo passes the browser picker but fails the backend's
  // multer fileFilter after a full upload round-trip). See
  // utils/fileValidation.js.
  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const error = validateFile(file, { maxSizeBytes: 10 * 1024 * 1024 });
    if (error) {
      toast.error(error);
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: file }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAddLicense = () => {
    if (
      formData.licenseName &&
      formData.issuingOrganization &&
      formData.licenseIssuingDate &&
      formData.licenseCredentialId
    ) {
      const newLicense = {
        id: Date.now(),
        name: formData.licenseName,
        organization: formData.issuingOrganization,
        issuingDate: formData.licenseIssuingDate,
        credentialId: formData.licenseCredentialId,
      };
      setFormData({
        ...formData,
        additionalLicenses: [...formData.additionalLicenses, newLicense],
        licenseName: "",
        issuingOrganization: "",
        licenseIssuingDate: "",
        licenseCredentialId: "",
      });
      setErrors({});
    }
  };

  const handleDeleteLicense = (licenseId) => {
    setFormData({
      ...formData,
      additionalLicenses: formData.additionalLicenses.filter(
        (license) => license.id !== licenseId,
      ),
    });
  };

  const handleBecomeInfepMember = () => {
    setFormData({ ...formData, isInfepRegistered: true });
    alert("Redirecting to INFEP registration...");
  };

  const handleRemoveCourse = (course) => {
    setFormData({
      ...formData,
      selectedCourses: formData.selectedCourses.filter((c) => c !== course),
    });
  };

  // const handleLocationChange = (e) => {
  //   setFormData({ ...formData, location: e.target.value });
  //   if (errors.location) setErrors({ ...errors, location: "" });
  // };
  const handleLocationChange = (newLocation) => {
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        ...newLocation, // if you're passing object
      },
    }));
  };

  const nextStep = async () => {
    const validationErrors = validateForm(currentStep);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (currentStep < totalSteps) {
      // A completed section is the natural checkpoint — save it (including
      // any files picked on this step) before moving on. registrationStep
      // records where to RESUME (the next step), not the one just finished.
      isDirtyRef.current = true;
      await saveProgress({ includeFiles: true, step: currentStep + 1 });
      setCurrentStep(currentStep + 1);
    } else {
      setShowCompletionModal(true);
      // console.log("Registration completed:", formData);
      // handleFinalSubmit();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  // Previously just bumped currentStep with no save at all — whatever was
  // filled in (including the logo File and any documents) was lost on
  // navigation, since the debounced autosave explicitly excludes files. Now
  // it saves exactly like "Continue" does before advancing.
  const skipStep = async () => {
    if (currentStep < totalSteps) {
      isDirtyRef.current = true;
      await saveProgress({ includeFiles: true, step: currentStep + 1 });
      setCurrentStep(currentStep + 1);
    }
  };

  // Previously just moved the user BACKWARDS and saved nothing, despite the
  // label — see TutorRegistration.jsx's identical fix.
  const handleSaveForLater = async () => {
    isDirtyRef.current = true;
    await saveProgress({ includeFiles: true, step: currentStep });
    toast.success("Progress saved — come back anytime to continue.");
    navigate("/");
  };

  const handleProceedToHome = () => {
    setShowCompletionModal(false);
    // Redirect logic for academy
    navigate("/academy-home");
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            handleInputChange={handleInputChange}
            handleLogoChange={handleLogoChange}
            handleIdChange={handleFileChange}
            registrationType="academy"
            academyName={academyName}
          />
        );
      case 2:
        return (
          <Step2
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            handleInputChange={handleInputChange}
            handleAddLicense={handleAddLicense}
            handleDeleteLicense={handleDeleteLicense}
            handleFileChange={handleFileChange}
            registrationType="academy"
          />
        );
      case 3:
        return (
          <StepContact
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
          />
        );
      case 4:
        return (
          <StepContact
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
          />
        );
      case 5:
        return (
          <Step5
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            handleInputChange={handleInputChange}
          />
        );
      default:
        return null;
    }
  };

  const handleFinalSubmit = async () => {
    const rateWarnings = findUnusualRateWarnings(formData.categoryDetails);
    if (rateWarnings.length > 0) {
      const confirmed = window.confirm(
        `${rateWarnings.join("\n")}\n\nUnusually low rates get flagged for admin review. Submit anyway?`,
      );
      if (!confirmed) return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const data = buildFormData({ includeFiles: true, includeCompletion: true });

      // Retry transient failures (dropped connection, 5xx) a couple of
      // times before giving up — same reasoning as TutorRegistration.jsx.
      const MAX_ATTEMPTS = 3;
      let res;
      for (let attempt = 1; ; attempt++) {
        try {
          res = await api.put("/users/me", data);
          break;
        } catch (err) {
          const isNetworkError = !err.response;
          const isServerError = err.response?.status >= 500;
          if (!(isNetworkError || isServerError) || attempt >= MAX_ATTEMPTS) {
            throw err;
          }
          toast(`Connection issue — retrying… (${attempt}/${MAX_ATTEMPTS - 1})`, {
            icon: "🔄",
          });
          await new Promise((r) => setTimeout(r, attempt * 1500));
        }
      }

      if (res.data.user) {
        updateUser(res.data.user);
      }

      toast.success("Academy registration completed successfully!");
      setShowCompletionModal(true);
    } catch (err) {
      console.error("Academy submit error:", err);
      const resData = err.response?.data;
      const message = resData?.message || "Failed to complete registration";
      // See TutorRegistration.jsx — the backend attaches a TL-PROFILE-###
      // reference for every save failure now.
      const display = resData?.code ? `${message} (Ref: ${resData.code})` : message;
      toast.error(display, { duration: 6000 });
      setErrors({ submit: display });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="registration-section">
        <div className="logo-container">
          <img
            src="../images/tutors-logo.png"
            alt="Website Logo"
            className="logo"
          />
        </div>
        <div className="registration-form-container">
          <div className="progress-bar">
            {Array.from({ length: totalSteps }, (_, index) => (
              <span
                key={index + 1}
                className={`progress-line ${
                  index + 1 <= currentStep ? "active-line" : ""
                }`}
              ></span>
            ))}
          </div>

          {saveStatus !== "idle" && (
            <div className={`autosave-status autosave-status--${saveStatus}`}>
              {saveStatus === "saving" && "Saving…"}
              {saveStatus === "saved" && "✓ All changes saved"}
              {saveStatus === "error" && "⚠ Couldn't save — will retry"}
            </div>
          )}

          {currentStep > 1 && (
            <div className="back-container">
              <button onClick={prevStep} className="back-btn">
                <FaArrowLeft />
                <span>Back</span>
              </button>
            </div>
          )}

          {currentStep === 1 && (
            <h1 className="title">
              Welcome {academyName}, let's set up your training center!
            </h1>
          )}

          <div className="form-section">
            <form
              // onSubmit={(e) => {
              //   e.preventDefault();
              //   nextStep();
              // }}
              onSubmit={(e) => {
                e.preventDefault();
                currentStep === totalSteps ? handleFinalSubmit() : nextStep();
              }}
            >
              {renderStep()}
              <div className="button-container">
                {currentStep === 1 ? (
                  <button type="button" onClick={skipStep} className="skip-btn">
                    Skip
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSaveForLater}
                    className="save-later-btn"
                  >
                    Save and Continue Later
                  </button>
                )}
                <button
                  type="submit"
                  className="continue-btn"
                  disabled={isLoading}
                >
                  {/* {currentStep === totalSteps ? "Submit" : "Continue"}{" "} */}
                  {isLoading
                    ? "Submitting..."
                    : currentStep === totalSteps
                      ? "Complete Registration"
                      : "Continue"}
                  <span className="arrow">→</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <SuccessModal
        show={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onProceed={handleProceedToHome}
        title="Welcome to the TutorsLinks! 🤗"
        subtitle="Your center registration is complete! Our team will review it soon. You'll be up and running in no time. Thank you for joining us!"
      />

      {currentStep === 1 && <AnalysisSection1 />}
      {currentStep === 2 && <AnalysisSection2 />}
      {currentStep === 3 && <AnalysisSection3 />}
      {currentStep === 4 && <AnalysisSection4 />}
      {currentStep === 5 && <AnalysisSection5 />}
    </div>
  );
};

export default AcademyRegistration;
